import { useCallback, useEffect, useRef, useState } from 'react'
import cookie from 'react-cookies'
import { authFetch, fetchWithAuth, BASE_BACKEND_URL, isAuthRedirectError } from '../auth'
import { DEFAULT_POS_REPORT_PRODUCT_GROUP, TASK_TYPES } from './constants/constants'

const emptyPage = () => ({
  options: [],
  page: 0,
  search: '',
  optionGroup: '',
  loading: false,
  hasMore: false,
  error: ''
})

export default function usePosReportOptions() {
  const [pages, setPages] = useState({})
  const pagesRef = useRef({})
  const scope = useRef(null)
  const surveyScope = useRef(null)
  const requests = useRef({})
  const publish = useCallback((next) => {
    pagesRef.current = next
    setPages(next)
  }, [])
  const cancel = useCallback((group) => {
    requests.current[group]?.abort()
    delete requests.current[group]
  }, [])
  useEffect(() => () => Object.values(requests.current).forEach((request) => request.abort()), [])

  const fetchPage = useCallback(
    async (
      group,
      search = '',
      page = 1,
      optionGroup = pagesRef.current[group]?.optionGroup || ''
    ) => {
      const context = group === TASK_TYPES.survey.key ? surveyScope.current : scope.current
      if (!context) return
      cancel(group)
      const controller = new AbortController()
      requests.current[group] = controller
      const previous = pagesRef.current[group] || emptyPage()
      publish({
        ...pagesRef.current,
        [group]: {
          ...previous,
          options: page === 1 ? [] : previous.options,
          search,
          optionGroup,
          loading: true,
          error: ''
        }
      })
      try {
        const params = {
          businesses: cookie.load('business_id'),
          task_type: context.taskType,
          start_date: context.period.Start,
          end_date: context.period.End,
          filter: group,
          page,
          per_page: 25,
          search
        }
        if (group === 'product' && optionGroup) params.product_group = optionGroup
        let data
        if (group === TASK_TYPES.survey.key) {
          const response = await authFetch(
            `${BASE_BACKEND_URL}/pos_downloads?survey_filter_info=true`,
            {
              method: 'POST',
              signal: controller.signal,
              headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
              body: JSON.stringify({ ...params, clients: context.clients, users: context.users })
            }
          )
          if (!response || controller.signal.aborted) return
          if (!response.ok) throw new Error('No se pudieron cargar las encuestas.')
          data = await response.json()
        } else {
          data = await fetchWithAuth(
            `${BASE_BACKEND_URL}/pos_downloads?${new URLSearchParams({ ...params, download_filter_info: 'true' })}`,
            {
              signal: controller.signal
            }
          )
        }
        if (controller.signal.aborted || requests.current[group] !== controller) return
        if (!data || data.error)
          throw new Error(data?.error || 'No se pudieron cargar las opciones.')
        const incoming = (data.result || []).map((item) => ({
          value: item.value,
          label: item.label || item.value
        }))
        const options = page === 1 ? incoming : [...previous.options, ...incoming]
        publish({
          ...pagesRef.current,
          [group]: {
            options: [...new Map(options.map((item) => [item.value, item])).values()],
            page,
            search,
            optionGroup,
            loading: false,
            hasMore: Boolean(data.has_more),
            error: ''
          }
        })
      } catch (error) {
        if (
          controller.signal.aborted ||
          isAuthRedirectError(error) ||
          requests.current[group] !== controller
        )
          return
        publish({
          ...pagesRef.current,
          [group]: {
            ...pagesRef.current[group],
            loading: false,
            error:
              group === TASK_TYPES.survey.key
                ? 'No se pudieron cargar las encuestas. Vuelve a intentar.'
                : 'No se pudieron cargar las opciones. Vuelve a intentar.'
          }
        })
      }
    },
    [cancel, publish]
  )

  const configure = useCallback(
    (taskType, period) => {
      Object.keys(requests.current).forEach(cancel)
      scope.current = taskType && period ? { taskType, period } : null
      surveyScope.current = null
      publish({})
      if (!scope.current) return
      const groups = ['user']
      if (
        ![
          TASK_TYPES.client_application.key,
          TASK_TYPES.users_start_end_day.key,
          TASK_TYPES.refund_limit.key
        ].includes(taskType)
      )
        groups.push('client')
      if (
        [
          TASK_TYPES.inventory.key,
          TASK_TYPES.planimetry.key,
          TASK_TYPES.expiration_date.key
        ].includes(taskType)
      )
        groups.push('product')
      groups.forEach((group) =>
        fetchPage(group, '', 1, group === 'product' ? DEFAULT_POS_REPORT_PRODUCT_GROUP : '')
      )
    },
    [cancel, fetchPage, publish]
  )

  const configureSurveys = useCallback(
    (selections, period) => {
      cancel(TASK_TYPES.survey.key)
      publish({ ...pagesRef.current, survey: emptyPage() })
      surveyScope.current =
        selections && period ? { taskType: TASK_TYPES.survey.key, period, ...selections } : null
      if (surveyScope.current) fetchPage(TASK_TYPES.survey.key)
    },
    [cancel, fetchPage, publish]
  )

  const selectOptionGroup = useCallback(
    (group, optionGroup) => fetchPage(group, '', 1, optionGroup),
    [fetchPage]
  )
  const search = useCallback((group, query) => fetchPage(group, query, 1), [fetchPage])
  const loadMore = useCallback(
    (group) => {
      const current = pagesRef.current[group]
      if (current?.hasMore && !current.loading) fetchPage(group, current.search, current.page + 1)
    },
    [fetchPage]
  )
  const retry = useCallback(
    (group) => {
      const current = pagesRef.current[group] || emptyPage()
      fetchPage(group, current.search, current.options.length ? current.page + 1 : 1)
    },
    [fetchPage]
  )
  return { pages, configure, configureSurveys, search, loadMore, retry, selectOptionGroup }
}
